<template>
	<span
		ref="editor"
		:data-key="datakey"
		class="fd-editable"
		:style="inputStyle()"
		role="textbox"
    	:tabindex="tabindex"
		@input="inputChanged"
		:contenteditable="canEdit">{{ modelValue }}</span>
</template>

<script setup lang="ts">
import {computed, type CSSProperties, ref, watch} from "vue";

const props = defineProps({
  modelValue: {
    type: String,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  minWidth: {
    type: Number,
    default: 2,
  },
  datakey: {
    type: [String, Object, Number],
  },
  placeholder: {
    type: String,
    default: ""
  },
  tabindex: {
    type: Number,
    default: 0,
  }
});
const emit = defineEmits(["update:modelValue"]);


const editor = ref<HTMLSpanElement | null>(null);
const lastCaretPosition = ref(0);
const lastContent = ref(props.modelValue);

const canEdit = computed(() => !props.disabled);

watch(() => props.modelValue, (newVal) => {
  if (newVal === lastContent.value) {
    // modelValue was reactively set back
    setCaretPosition(editor.value, lastCaretPosition.value);
  }
}, {
  flush: "post"
});

function inputStyle():CSSProperties {
  const s: CSSProperties = {};
  if (props.minWidth > 0) {
    s["minWidth"] = props.minWidth + "px";
  }
  if (props.placeholder && !props.modelValue) {
    s["backgroundImage"] = "url(" + generateSvgUrl(props.placeholder, "#AAAAAA") + ")";
  }
  return s;
}

function getCaretPosition(editableElement:HTMLSpanElement | null) {
  let caretPos = 0, sel, range;
  if (window.getSelection) {
    sel = window.getSelection();
    if (sel && sel.rangeCount) {
      range = sel.getRangeAt(0);
      if (range.commonAncestorContainer.parentNode === editableElement) {
        caretPos = range.endOffset;
      }
    }
  }
  return caretPos;
}

function setCaretPosition(editableElement:HTMLSpanElement | null, pos:number) {
  let range = document.createRange();
  let sel = window.getSelection();

  if (editableElement) {
    const chn = editableElement.childNodes;
    if (chn.length > 0) {
      range.setStart(chn[0], pos);
      range.collapse(true);

      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  }
}

function inputChanged(event:Event) {
  lastCaretPosition.value = getCaretPosition(editor.value);
  const value = editor.value ? editor.value.innerText : "";
  lastContent.value = value;
  emit("update:modelValue", value);
}

function focus() {
  if (editor.value) {
    editor.value.focus();
  }
}

defineExpose({ focus });

function generateSvgText(text: string, color: string):string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="200px" height="18px" viewBox="0 0 200 18">
    <text x="2" y="14" fill="${color}" style="font-family:'Helvetica Neue',sans-serif;font-weight:300;font-size:14px">${text}</text>
    </svg>`;
  return svg;
}

function generateSvgUrl(text:string, color: string):string {
  const svg = generateSvgText(text, color);
  const encoded = window.btoa(svg);
  return "data:image/svg+xml;base64,"+encoded
}
</script>

<style>
.fd-editable {
	box-sizing: border-box;
	border: none;
	padding: 0 2px;
	outline: none;
	opacity: 0.25;
	background-image: none;
	background-repeat: no-repeat;
	background-position: left center;
}

.fd-editable[contenteditable="true"] {
	opacity: 1;
}

.fd-editable[contenteditable="true"]:empty:not(:focus) {
}
/* Workaround Safari baseline alignment of empty elements */
.fd-editable:empty::before {
	content:'\200B';	/* This is &ZeroWidthSpace; entity */
}
.fd-editable:last-child {
	flex-grow: 1;
}
</style>
